import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { UserCircle, Buildings, CalendarStar, Cake, Megaphone, Trophy, ClockClockwise, Warning } from "@phosphor-icons/react";

function flatten(nodes, depth = 0, out = []) {
  for (const n of nodes) {
    out.push({ ...n, depth });
    if (n.children && n.children.length) flatten(n.children, depth + 1, out);
  }
  return out;
}

function OrgTree({ nodes }) {
  const flat = flatten(nodes);
  return (
    <div>
      {flat.map((n) => (
        <div key={n.id} className="flex items-center gap-3 py-1.5" style={{ paddingLeft: `${n.depth * 24}px` }}>
          {n.depth > 0 && <span className="text-emerald-400">└</span>}
          <div className="w-9 h-9 rounded-full bg-emerald-700 text-white grid place-items-center text-xs font-bold">
            {(n.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-medium">{n.name}</div>
            <div className="text-xs text-zinc-500">{n.designation || "—"} · {n.department || "—"} · <span className="font-mono-plex">{n.code}</span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CompanyHub() {
  const { user, tenantId } = useAuth();
  const [org, setOrg] = useState([]);
  const [celebs, setCelebs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const canHR = ["super_admin", "company_admin", "hr"].includes(user?.role);

  useEffect(() => {
    const p = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/org-chart", { params: p }).then(({ data }) => setOrg(data)).catch(() => {});
    api.get("/celebrations", { params: p }).then(({ data }) => setCelebs(data)).catch(() => {});
    api.get("/announcements", { params: p }).then(({ data }) => setAnnouncements(data)).catch(() => {});
    api.get("/kudos/leaderboard", { params: p }).then(({ data }) => setLeaderboard(data)).catch(() => {});
    api.get("/time-off-calendar", { params: p }).then(({ data }) => setTimeOff(data)).catch(() => {});
    if (canHR) {
      api.get("/compliance-calendar", { params: p }).then(({ data }) => setCalendar(data)).catch(() => {});
      api.get("/audit-log", { params: p }).then(({ data }) => setAuditLog(data)).catch(() => {});
    }
  }, [tenantId, user?.role, canHR]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Company</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Company hub</h1>
        <p className="text-sm text-zinc-500 mt-2">Org chart, announcements, celebrations, kudos leaderboard, time-off calendar and compliance radar.</p>
      </div>

      <Tabs defaultValue="org">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm flex-wrap h-auto">
          <TabsTrigger value="org" className="rounded-sm"><Buildings size={14} className="mr-1" /> Org chart</TabsTrigger>
          <TabsTrigger value="feed" className="rounded-sm"><Megaphone size={14} className="mr-1" /> Announcements</TabsTrigger>
          <TabsTrigger value="celebrations" className="rounded-sm"><Cake size={14} className="mr-1" /> Celebrations</TabsTrigger>
          <TabsTrigger value="leaderboard" className="rounded-sm"><Trophy size={14} className="mr-1" /> Kudos</TabsTrigger>
          <TabsTrigger value="timeoff" className="rounded-sm"><CalendarStar size={14} className="mr-1" /> Who's out</TabsTrigger>
          {canHR && <TabsTrigger value="calendar" className="rounded-sm"><ClockClockwise size={14} className="mr-1" /> Compliance</TabsTrigger>}
          {canHR && <TabsTrigger value="audit" className="rounded-sm"><Warning size={14} className="mr-1" /> Audit log</TabsTrigger>}
        </TabsList>

        <TabsContent value="org" className="mt-4">
          <div className="border border-zinc-200 bg-white rounded-sm p-5 max-h-[70vh] overflow-auto">
            {org.length === 0 ? <div className="text-sm text-zinc-500 text-center py-8">No org data.</div> : <OrgTree nodes={org} />}
          </div>
        </TabsContent>

        <TabsContent value="feed" className="mt-4 space-y-3">
          {announcements.length === 0 && <div className="border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500 rounded-sm">No announcements yet.</div>}
          {announcements.map((a) => (
            <div key={a.id} className={`border rounded-sm p-4 bg-white ${a.pinned ? "border-emerald-300" : "border-zinc-200"}`}>
              <div className="flex items-start justify-between">
                <div>
                  {a.pinned && <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200 mb-1">Pinned</Badge>}
                  <div className="font-display text-lg font-bold">{a.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{a.author_name} · {new Date(a.created_at).toLocaleDateString()}</div>
                </div>
                <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300">{a.kind}</Badge>
              </div>
              <div className="mt-3 text-sm text-zinc-700 whitespace-pre-line">{a.body}</div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="celebrations" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {celebs.length === 0 && <div className="col-span-full border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500 rounded-sm">Nothing coming up in the next 30 days.</div>}
            {celebs.map((c, i) => (
              <div key={i} className="border border-zinc-200 rounded-sm p-4 bg-white">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-sm grid place-items-center ${c.kind === "birthday" ? "bg-orange-50 text-orange-700" : "bg-emerald-50 text-emerald-700"}`}>
                    {c.kind === "birthday" ? <Cake size={20} weight="duotone" /> : <Trophy size={20} weight="duotone" />}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500">{c.kind}{c.years ? ` · ${c.years} yr` : ""}</div>
                    <div className="font-display font-bold">{c.employee_name}</div>
                    <div className="text-xs text-zinc-500 font-mono-plex">{c.date} · {c.department}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-4">
          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="hr-table">
              <thead><tr><th>Rank</th><th>Employee</th><th>Kudos count</th><th>Points</th></tr></thead>
              <tbody>
                {leaderboard.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-zinc-500 text-sm">No kudos yet — be the first to recognise someone!</td></tr>}
                {leaderboard.map((r, i) => (
                  <tr key={i}>
                    <td className="font-display text-lg font-bold">{i + 1}</td>
                    <td><div className="text-sm">{r.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div></td>
                    <td className="font-mono-plex text-xs">{r.kudos_count}</td>
                    <td className="font-mono-plex text-lg font-bold text-emerald-700">{r.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="timeoff" className="mt-4">
          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="hr-table">
              <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th></tr></thead>
              <tbody>
                {timeOff.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-zinc-500 text-sm">Everyone's in this fortnight.</td></tr>}
                {timeOff.map((r) => (
                  <tr key={r.id}>
                    <td><div className="text-sm">{r.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div></td>
                    <td className="capitalize">{r.leave_type}</td>
                    <td className="font-mono-plex text-xs">{r.start_date}</td>
                    <td className="font-mono-plex text-xs">{r.end_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {canHR && (
          <TabsContent value="calendar" className="mt-4">
            <div className="overflow-x-auto border border-zinc-200 rounded-sm">
              <table className="hr-table">
                <thead><tr><th>Tenant</th><th>Deadline</th><th>Type</th><th>Period</th><th>Days left</th><th>Status</th></tr></thead>
                <tbody>
                  {calendar.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500 text-sm">Nothing scheduled.</td></tr>}
                  {calendar.map((r, i) => (
                    <tr key={i}>
                      <td className="text-sm">{r.tenant_name}</td>
                      <td className="font-mono-plex text-xs">{r.deadline}</td>
                      <td>{r.label}</td>
                      <td className="font-mono-plex text-xs">{r.period_month}</td>
                      <td>
                        <Badge variant="outline" className={`rounded-sm ${{
                          overdue: "bg-red-50 text-red-800 border-red-200",
                          urgent: "bg-orange-50 text-orange-800 border-orange-200",
                          upcoming: "bg-amber-50 text-amber-800 border-amber-200",
                          planned: "bg-zinc-50 text-zinc-700 border-zinc-200",
                        }[r.urgency] || ""}`}>{r.days_left}d · {r.urgency}</Badge>
                      </td>
                      <td className="text-xs">{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {canHR && (
          <TabsContent value="audit" className="mt-4">
            <div className="border border-zinc-200 bg-white rounded-sm max-h-[70vh] overflow-auto">
              {auditLog.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No audit events.</div>}
              {auditLog.map((e, i) => (
                <div key={i} className="px-4 py-2.5 border-t border-zinc-100 first:border-t-0 flex items-center gap-3">
                  <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300 font-mono-plex text-[10px] uppercase">{e.kind}</Badge>
                  <div className="flex-1 text-sm text-zinc-800">{e.message}</div>
                  <div className="text-[10px] text-zinc-500 font-mono-plex">{e.at ? new Date(e.at).toLocaleString() : "—"}</div>
                </div>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
